import axios from 'axios';
import {
  ACCESS_MODES,
  isReadEndpoint,
  VIEW_ONLY_MESSAGE,
} from '../constants/readOnlyAccess';
import { HEALTH } from '../components/global/Server/endpoints';
import { updateSelectedUserId } from './actions/auth';

// --- Server reachability -------------------------------------------------
// Tracks whether the backend is answering at all, so the header's
// "Service Disruption" banner can be driven by plain HTTP failures
// instead of only by the RFID WebSocket.
//
// The socket-only signal was the bug: Base.jsx's initConnections skips
// the RFID socket entirely for ROLE_ADMIN / ROLE_MASTER_ADMIN, tech
// admins (general socket only, which never sets serverStatus),
// ROLE_READING users, and anyone without a selectedLocationId — so for
// those roles serverStatus stayed '' forever and the banner was
// unreachable no matter how dead the server was.
//
// Module-level rather than a static class field: this file is a plain
// static utility used outside the React tree, and the state must be
// process-wide, not per-caller.
let serverReachable = true;

const OUTAGE_STATUSES = [502, 503, 504];

// A dashboard mount can fire a dozen requests at once; when the server
// dies they all fail together. Share one in-flight probe between them so
// a single outage costs one extra request, not a dozen.
let probeInFlight = null;

// --- App-switch suppression ---------------------------------------------
// A backgrounded tab is not an outage. When the OS moves the browser (or
// the installed PWA) off screen it aborts in-flight XHRs, closes
// WebSockets and freezes timers; every one of those surfaces here as a
// request that "never got an answer" — exactly the shape of a real
// outage. Without this guard, switching to another app and coming back
// marked the backend DOWN and then UP a moment later, and a confirmed
// recovery reloads the page.
//
// Resuming is nearly as noisy as being hidden: the first request out of a
// radio that has just woken up often fails before the connection is
// really back, so for a short window after the app returns the probe has
// to prove itself twice before we believe it.
const RESUME_GRACE_MS = 8000;
const RESUME_RECHECK_DELAY_MS = 1500;

let lastResumedAt = 0;

if (typeof document !== 'undefined' && document.addEventListener) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') lastResumedAt = Date.now();
  });
}

const pageIsHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

// Explicitly false only when the browser is certain there is no network;
// undefined (unsupported) must not read as offline.
const deviceIsOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

const justResumed = () =>
  lastResumedAt !== 0 && Date.now() - lastResumedAt < RESUME_GRACE_MS;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const emit = (name, detail) => {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    }
  } catch (e) {
    // Best-effort. A missing event only costs us the banner; it never
    // affects the request that triggered it.
  }
};

class AuthUtil {
  static getToken() {
    return localStorage.getItem('token');
  }

  // Access mode for the location the user is currently operating:
  // 'FULL' or 'VIEW'. Stamped by the auth saga from the server's
  // getUser response on login and on every location switch, and kept in
  // localStorage (rather than read from redux) because this class is a
  // static utility outside the React tree — same reason
  // selectedCompanyId lives here. Defaults to FULL so an older cached
  // session behaves exactly as it did before this feature.
  static getAccessMode() {
    return localStorage.getItem('accessMode') || ACCESS_MODES.FULL;
  }

  static isViewOnly() {
    return this.getAccessMode() === ACCESS_MODES.VIEW;
  }

  /**
   * Refuse a write before it leaves the browser when the user is at a
   * VIEW-ONLY location.
   *
   * Greyed-out controls handle the visible cases; this handles the rest —
   * auto-save timers, retry queues, keyboard shortcuts, and any control
   * added later that nobody remembered to guard. Together they mean a
   * viewer simply cannot write, rather than being unable to write from
   * the places we happened to sweep.
   *
   * The server enforces this too (ReadOnlyGateInterceptor) and remains
   * the authority — a modified client is not the threat model here. The
   * point of doing it locally is an instant, explanatory refusal instead
   * of a round-trip and a generic failure.
   *
   * Throws an error shaped exactly like the server's 403 response, so
   * every existing `catch` path behaves the same whether the refusal came
   * from here or from the backend.
   */
  static guardWrite(endpoint) {
    if (!this.isViewOnly()) return;
    if (isReadEndpoint(endpoint)) return;

    const data = {
      success: false,
      readOnly: true,
      accessMode: ACCESS_MODES.VIEW,
      message: VIEW_ONLY_MESSAGE,
    };

    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('app:readonly', { detail: data }));
      }
    } catch (e) {
      // Best-effort toast; the refusal below is what matters.
    }

    const error = new Error(VIEW_ONLY_MESSAGE);
    error.response = { status: 403, data };
    error.isReadOnlyRefusal = true;
    throw error;
  }

  /**
   * The server refused a shift-bound write because the employee's shift is
   * already closed (`code: 'NOT_CLOCKED_IN'`).
   *
   * Handled centrally for the same reason 423/403 are: the refusal can come
   * back from any of the guarded endpoints (ticket out, bonus, raffle,
   * transaction, cash left, points, transfer accept), and threading a code
   * through every saga and every save callback would mean six chances to
   * forget one.
   *
   * Unlike a lock or a read-only refusal this arrives as HTTP 200 with
   * `success:false` — the request was well-formed and authenticated, the
   * shift just isn't open — so it is inspected on the SUCCESS path.
   *
   * The sagas still surface the server's own message as their toast; this
   * adds the part they can't do, which is getting the user to the screen
   * where they can fix it.
   */
  static checkShiftClosed(response) {
    const data = response && response.data;
    if (!data || data.success !== false) return;
    if (data.code !== 'NOT_CLOCKED_IN') return;

    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('app:shift-closed', { detail: data })
        );
      }
    } catch (e) {
      // Best-effort — the write was already refused server-side, so a
      // missing redirect is cosmetic, not a correctness problem.
    }
  }

  static isTokenValid() {
    const token = this.getToken();
    return !!token;
  }

  // Returns the companyId the user is currently operating in. Set by
  // CompanyPicker on every switch. The backend's LockGateInterceptor and
  // AccountService validate this against the user's allowed list, so a
  // tampered value can't reach a company the user isn't linked to —
  // worst case it falls back to the user's home company.
  static getSelectedCompanyId() {
    return localStorage.getItem('selectedCompanyId') || '';
  }

  // Header bundle injected into every authenticated request so the
  // backend evaluates the correct company for lock state, license
  // checks, and any other per-company gating. Empty string → header
  // omitted so the server falls back to the home company.
  static authHeaders(extra = {}) {
    const selectedCompanyId = this.getSelectedCompanyId();
    const headers = {
      Authorization: `Bearer ${this.getToken()}`,
      ...extra,
    };
    if (selectedCompanyId) {
      headers['X-Selected-Company'] = selectedCompanyId;
    }
    return headers;
  }

  static isServerReachable() {
    return serverReachable;
  }

  /**
   * Classify a failed request as "the backend is unreachable" vs "the
   * backend answered and said no".
   *
   * Kept deliberately narrow. A 4xx, a 500, or a business-logic failure
   * all mean the server is alive and talking, so they must NOT raise the
   * outage banner. Only a request that never got an answer (connection
   * refused / reset mid-restart) or a gateway-level 502/503/504 counts.
   *
   * Cancelled requests and client-side timeouts are excluded too: a
   * 120s photo upload aborting on a weak in-store connection is not an
   * outage, and treating it as one would cry wolf on exactly the users
   * who most need the banner to mean something.
   */
  static isOutageError(error) {
    if (!error) return false;
    // Locally-thrown read-only refusal — never left the browser.
    if (error.isReadOnlyRefusal) return false;
    if (axios.isCancel && axios.isCancel(error)) return false;
    if (error.response) {
      return OUTAGE_STATUSES.includes(error.response.status);
    }
    return error.code !== 'ECONNABORTED';
  }

  // Any answer from the server — even an error one — proves it is up.
  static noteServerReachable() {
    if (serverReachable) return;
    serverReachable = true;
    emit('app:server-up');
  }

  // Flips to DOWN only once the probe agrees, so a single blip can't
  // flash a red banner across every terminal in the building.
  static async noteServerUnreachable(reason) {
    if (!serverReachable) return;

    // The app is off screen, or the device has no network at all — see
    // the RESUME_GRACE_MS block above. Failures in either state say
    // nothing about the backend, and treating them as an outage is what
    // turned an app switch into "Connection restored" and a page reload.
    if (pageIsHidden() || deviceIsOffline()) return;

    let alive = await this.probeHealth();

    // The probe itself can be cut off by the app being backgrounded while
    // it was in flight, so re-check instead of trusting a stale answer.
    if (alive || pageIsHidden() || deviceIsOffline()) return;

    if (justResumed()) {
      await wait(RESUME_RECHECK_DELAY_MS);
      if (pageIsHidden() || deviceIsOffline()) return;
      alive = await this.probeHealth();
      if (alive) return;
    }

    if (serverReachable === false) return;
    serverReachable = false;
    emit('app:server-down', { reason: reason || 'network' });
  }

  /**
   * Unauthenticated liveness check against /health/ping.
   *
   * Resolves true when the server answers anything that isn't a gateway
   * error — during a Grails restart nginx returns 502/503 while the app
   * boots, and that window is exactly what the banner is for.
   *
   * Bare axios, not getRequest(): no Bearer token (an expired one must
   * not read as an outage) and no recursion back into checkAuthError.
   */
  static async probeHealth() {
    if (probeInFlight) return probeInFlight;
    probeInFlight = this.runHealthProbe().finally(() => {
      probeInFlight = null;
    });
    return probeInFlight;
  }

  static async runHealthProbe() {
    try {
      // Cache-busting query param rather than a Cache-Control header:
      // a custom header would make this a preflighted cross-origin
      // request, and an extra OPTIONS round-trip is the last thing we
      // want on the path that decides whether the server is alive.
      const response = await axios.get(`${HEALTH.PING}?t=${Date.now()}`, {
        timeout: 5000,
      });
      return !OUTAGE_STATUSES.includes(response.status);
    } catch (error) {
      if (error && error.response) {
        return !OUTAGE_STATUSES.includes(error.response.status);
      }
      return false;
    }
  }

  static handleLogout() {
    localStorage.clear();
    window.location.replace('/login');
  }

  static async putRequest(endpoint, data) {
    this.guardWrite(endpoint);
    try {
      const response = await axios.put(endpoint, data, {
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      });
      this.noteServerReachable();
      this.checkShiftClosed(response);
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static async getRequest(endpoint) {
    try {
      const response = await axios.get(endpoint, {
        headers: this.authHeaders(),
      });
      this.noteServerReachable();
      this.checkShiftClosed(response);
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static async postUploadRequest(endpoint, data, onProgress) {
    this.guardWrite(endpoint);
    try {
      const response = await axios.post(endpoint, data, {
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            onProgress(progress);
          }
        },
      });
      this.noteServerReachable();
      this.checkShiftClosed(response);
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  // timeoutMs: callers that retry on their own schedule can ask for a shorter
  // ceiling than the 120s default. The bonus photo queue does — one worker
  // serves every pending photo, so a slow upload sitting on it for two minutes
  // is two minutes none of the others move.
  static async postMultipartRequest(
    endpoint,
    formData,
    timeoutMs,
    onUploadProgress
  ) {
    this.guardWrite(endpoint);
    try {
      const response = await axios.post(endpoint, formData, {
        headers: this.authHeaders(),
        // Fail hung uploads instead of waiting forever, so callers'
        // retry/outbox logic (e.g. bonus photo queue) can kick in.
        timeout: timeoutMs || 120000,
        // Bytes actually on the wire. The bonus photo queue turns this into a
        // real progress bar — on a weak in-store connection "it is moving, and
        // this is how far" is a different message from a spinner that could
        // equally mean stuck.
        onUploadProgress,
      });
      this.noteServerReachable();
      this.checkShiftClosed(response);
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  static async postRequest(endpoint, data) {
    this.guardWrite(endpoint);
    try {
      const response = await axios.post(endpoint, data, {
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      });
      this.noteServerReachable();
      this.checkShiftClosed(response);
      return response;
    } catch (error) {
      this.checkAuthError(error);
      throw error;
    }
  }

  // Write the persisted redux state to disk NOW, for a caller that is
  // about to reload the page.
  //
  // redux-persist writes on a later tick, so a dispatch followed by a
  // reload is a race, and the reload usually wins. Every location switch
  // in this app is "dispatch, then reload" — so without this, the page
  // that comes back can rehydrate the PREVIOUS location: localStorage
  // (the X-Selected-Company header) is written synchronously and is
  // correct, while `userInfo.selectedLocationId` — which screens put in
  // their request BODIES — is still the old one. The two then disagree
  // for a whole page load, and AccessScopeInterceptor checks both.
  //
  // Required lazily: store -> sagas -> AuthUtil, so a top-level import of
  // the store here would be circular.
  static flushPersistedState() {
    try {
      // eslint-disable-next-line global-require
      const { persistor } = require('./store');
      return persistor.flush().catch(() => {});
    } catch (e) {
      return Promise.resolve();
    }
  }

  // The OTHER copy of "which company am I in". The 409 branch below fixes
  // localStorage, which only feeds the X-Selected-Company header; screens
  // put `userInfo.selectedLocationId` (redux-persisted) in their request
  // BODIES, and AccessScopeInterceptor checks both. Left stale, the reload
  // rehydrates the revoked id, the bell polls with it, gets 409 again and
  // reloads again — a loop observed in production, ~1 reload/second.
  // Flushed before the reload so the corrected value is what rehydrates.
  static resetPersistedLocation(companyId, companyName) {
    if (!companyId) return Promise.resolve();
    try {
      // eslint-disable-next-line global-require
      const { store } = require('./store');
      store.dispatch(
        updateSelectedUserId({
          selectedLocationId: companyId,
          ...(companyName ? { selectedLocationName: companyName } : {}),
          // Home is always FULL — nobody is view-only at their own site.
          accessMode: 'FULL',
        })
      );
      return this.flushPersistedState();
    } catch (e) {
      return Promise.resolve();
    }
  }

  // Last line of defence against the loop above returning through some
  // other stale source: after a few scope reloads inside a minute, stop
  // reloading. The server keeps refusing, so the screen simply stays put
  // instead of hammering the API every second.
  static scopeReloadLooping() {
    try {
      const KEY = 'scopeReloadTimes';
      const now = Date.now();
      const recent = JSON.parse(sessionStorage.getItem(KEY) || '[]').filter(
        (t) => now - t < 60000
      );
      if (recent.length >= 3) return true;
      recent.push(now);
      sessionStorage.setItem(KEY, JSON.stringify(recent));
      return false;
    } catch (e) {
      return false;
    }
  }

  static checkAuthError(error) {
    // Outage check first: a request that never reached the server tells
    // us nothing about auth, and must not fall through to the 401/403
    // branch below. Fire-and-forget — the caller's error still
    // propagates immediately; the banner catches up a beat later.
    if (this.isOutageError(error)) {
      this.noteServerUnreachable(
        error.response ? `http-${error.response.status}` : 'network'
      );
      return;
    }

    // We got a real answer, so the server is alive — clear any banner a
    // previous failure raised, even if this particular answer is an
    // error one.
    this.noteServerReachable();

    // HTTP 403 + readOnly: ReadOnlyGateInterceptor refused a write
    // because the user is operating a VIEW-ONLY linked location. Their
    // session is perfectly valid — it just can't change anything here —
    // so this MUST be checked before the generic 401/403 branch below,
    // which logs the user out. Surface it as an event the app turns into
    // a toast; the user stays exactly where they are.
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data &&
      error.response.data.readOnly
    ) {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('app:readonly', { detail: error.response.data })
          );
        }
      } catch (e) {
        // Best-effort only — the write was already refused server-side,
        // so a missing toast is cosmetic.
      }
      return;
    }

    // HTTP 409 + scopeChanged: AccessScopeInterceptor refused a request
    // whose X-Selected-Company names a location this user no longer
    // holds — typically because another company's owner just revoked
    // their access while they were standing in it.
    //
    // Checked BEFORE the 401/403 branch for the same reason readOnly is:
    // the session is valid, so logging them out would be wrong. Reset to
    // the home company the server named and reload, which is exactly
    // what a deliberate switch in the picker does. Cannot be left to the
    // app to sort out later — until the header changes, every following
    // request is answered as the home company under the OLD company's
    // name, so the reload has to happen here.
    if (
      error.response &&
      error.response.status === 409 &&
      error.response.data &&
      error.response.data.scopeChanged
    ) {
      try {
        const { companyId, companyName } = error.response.data;
        if (companyId && typeof localStorage !== 'undefined') {
          localStorage.setItem('selectedCompanyId', companyId);
          // Home is always FULL — nobody is view-only at their own site.
          localStorage.setItem('accessMode', 'FULL');
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('app:scope-changed', {
              detail: error.response.data,
            })
          );
          // Guard against a reload storm if several in-flight requests
          // all come back 409 at once.
          if (!AuthUtil._scopeReloading && !AuthUtil.scopeReloadLooping()) {
            AuthUtil._scopeReloading = true;
            AuthUtil.resetPersistedLocation(companyId, companyName).then(() =>
              setTimeout(() => window.location.reload(), 150)
            );
          }
        }
      } catch (e) {
        // Best-effort. The server keeps refusing until the header
        // changes, so a missed reload retries on the next request.
      }
      return;
    }

    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      this.handleLogout();
    } else if (error.response && error.response.status === 423) {
      // HTTP 423 LOCKED: server-side LockGateInterceptor refused a
      // feature endpoint because the user's company is locked. This
      // is the FALLBACK path for sessions that didn't receive the
      // 'company-locked' WebSocket broadcast — typically admin-role
      // users whose Base.jsx initConnections skips WebSocket setup.
      //
      // We deliberately do NOT log the user out. Bearer tokens are
      // kept valid for billing-related locks so the user can use the
      // LockedShell's pay/activate flows without re-entering creds.
      // Instead we dispatch a window event that Base.jsx listens for
      // and re-fetches userInfo, which triggers the in-place
      // transition to LockedShell.
      try {
        const payload = error.response.data;
        const lockState = payload && payload.lockState;
        if (lockState && typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('app:locked', { detail: lockState })
          );
        }
      } catch (e) {
        // Best-effort. If the event dispatch fails we still keep
        // the user logged in; the next request will repeat the
        // 423 cycle until the listener fires successfully.
      }
    }
  }
}

export default AuthUtil;
